import type {
  PreviousJobInfo,
  PreviousProvider,
} from '@/features/jobs/drafting/PreviousProvider'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { JobsApi } from '@/features/jobs/api/job-api'
import { ListPreviousProvidersRequest } from '@taxfyle/api-internal/internal/draft_pb'
import { Id } from '@/infra/id'
import { resolveOnAbort, throwIfAborted } from '@/utils/abort-util'
import type { SearchableJobStore } from '@/features/jobs/list/SearchableJobStore'
import { observable } from 'mobx'

/**
 * Provides a way to retrieve previous providers.
 */
export class PreviousProviderStore {
  constructor(
    private readonly _api: JobsApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _searchStore: SearchableJobStore,
  ) {}

  /**
   * List previous providers for the current user.
   */
  async listPreviousProviders(
    signal?: AbortSignal,
  ): Promise<PreviousProvider[]> {
    const member = this._getCurrentMember()
    const currentUserId = Id.assert(member.userId)
    const currentWorkspaceId = Id.assert(member.workspaceId)

    const request = new ListPreviousProvidersRequest()
    request.setWorkspaceId(currentWorkspaceId)
    request.setUserPublicId(currentUserId)

    const response = await resolveOnAbort(
      this._api.draft.listPreviousProviders(request),
      signal,
    ).then(throwIfAborted)

    const list = response.getPreviousProvidersList()
    const providerUserIds = list.map((x) => x.getUserPublicId())
    if (!providerUserIds.length) {
      // No previous Pros found.
      return []
    }

    const workspaceFilter = `members.user_public_id:"${currentUserId}" AND workspace_id="${currentWorkspaceId}"`
    const userFilter = `(${providerUserIds
      .map((u) => `members.user_public_id:"${u}"`)
      .join(' OR ')})`
    const filter = [workspaceFilter, userFilter].join(' AND ')
    const jobs = await this._searchStore
      .list(
        {
          limit: 100,
          orderBy: 'date_completed desc,date_last_activity desc',
          filter,
          after: null,
        },
        signal,
      )
      .then((r) => r.data)

    const entries = jobs.map((job) => [job.championPro?.user.id, job] as const)
    // Make sure jobs that appear first take precedence.
    entries.reverse()
    const jobsMap = new Map(entries)

    return list.flatMap((proto) => {
      const jobForProvider = jobsMap.get(proto.getUserPublicId())
      if (!jobForProvider) {
        return []
      }

      const previousJobInfo: PreviousJobInfo = observable({
        id: jobForProvider.id,
        get title() {
          return jobForProvider.displayName
        },
      })

      const previousPro: PreviousProvider = {
        displayName: proto.getDisplayName(),
        avatarUrl: proto.getPicture(),
        userId: proto.getUserPublicId(),
        previousJobInfo,
      }
      return [previousPro]
    })
  }
}
